import {
  PublicListVendorResponse,
  PublicListCustomerProductResponse,
  SharedVendor,
  SharedCustomerProduct,
  PublicBulkValidateVoucherResponse,
  PublicListCustomerPackageCollectionResponse,
  SharedCustomerPackageCollection,
  V1ResponseMetadata,
} from '@bukalapak/openapi-schema/vptrader/product/v1/public/product-domain';
import { SharedVoucher } from '@bukalapak/openapi-schema/vptrader/product/v2/public/product-domain';
import uniqBy from 'lodash/uniqBy';
import countBy from 'lodash/countBy';
import AgenliteAPIClient from '~/api';
import VPNonTraderAPIClient from '~/api/vpnontrader';
import transformArrayMixin from '~/mixins/transformArray';

type InitialState = {
  customerList: [];
  vendorList: SharedVendor[];
  productList: SharedCustomerProduct[];
  chosenProduct: SharedCustomerProduct;
  productListMeta: V1ResponseMetadata;
  voucherList: SharedVoucher[];
  customerPackageCollections: SharedCustomerPackageCollection[];

  loading: {
    customerList: Boolean;
    productList: Boolean;
    chosenProduct: Boolean;
    voucherList: Boolean;
    vendorList: Boolean;
    customerPackageCollection: Boolean;
  };
};

interface IVoucher {
  voucher_code: string;
}

const initialState: InitialState = {
  customerList: [],
  vendorList: [],
  productList: [],
  chosenProduct: {},
  productListMeta: {},
  voucherList: [],
  customerPackageCollections: [],

  loading: {
    customerList: true,
    productList: true,
    chosenProduct: true,
    voucherList: true,
    vendorList: true,
    customerPackageCollection: true,
  },
};

const getters = {
  getCustomerList(state) {
    return state.customerList;
  },
  getCustomerNumberList(state, getters) {
    const customerPhoneList: any = [];
    state.customerList.forEach(customer => {
      customer?.numbers?.forEach(({ number, id, type, operator_id: operatorId }) => {
        const prefix = number.substring(0, 4);
        const vendor = getters.getPrefixVendorMapping?.[prefix];
        customerPhoneList.push({
          id,
          name: customer.name,
          number,
          logo: vendor?.logo_url,
          type,
          operatorId,
        });
      });
    });
    return customerPhoneList;
  },
  getVendorList(state) {
    return state.vendorList;
  },
  getPrefixVendorMapping(state) {
    const mapping = {};
    state.vendorList.forEach(v => {
      v.prefixes.forEach(p => {
        mapping[p] = v;
      });
    });
    return mapping;
  },
  getVendorFromPhone(_, getters) {
    return function (phoneNumber) {
      const mapping = getters.getPrefixVendorMapping;
      const prefix = Object.keys(mapping)
        .sort((a, b) => b.length - a.length)
        .find(prefix => phoneNumber.startsWith(prefix));

      return mapping[prefix!];
    };
  },
  getProductList(state) {
    return state.productList;
  },
  getChosenProduct(state) {
    return state.chosenProduct;
  },
  getProductListMeta(state) {
    return state.productListMeta;
  },
  getVoucherList(state) {
    return state.voucherList;
  },
  getLoadingState(state) {
    return state.loading;
  },
  getCustomerPackageCollections(state) {
    return state.customerPackageCollections;
  },
};

const actions = {
  async fetchCustomerList({ dispatch, commit }) {
    let response: any;
    commit('setLoadingState', { name: 'customerList', status: true });
    try {
      response = await AgenliteAPIClient.getAgentCustomersList();
      const { data: customers } = response;

      commit('setCustomerList', customers);
    } catch (error) {
      dispatch('catchError', { error });
    } finally {
      commit('setLoadingState', { name: 'customerList', status: false });
    }
    return response;
  },
  async fetchVendorList({ dispatch, commit }, { productType, phoneNumber, isSetVendorList = false }) {
    let response: PublicListVendorResponse = {};
    try {
      response = await VPNonTraderAPIClient.getVendors(productType, phoneNumber);
      if (!isSetVendorList) {
        commit(
          'setVendorList',
          response.vendors?.map(vendor => ({
            ...vendor,
            status: vendor.status === 'VENDOR_STATUS_AVAILABLE',
          }))
        );
      }
    } catch (error) {
      dispatch('catchError', { error });
    } finally {
      if (!isSetVendorList) {
        commit('setLoadingState', { name: 'vendorList', status: false });
      }
    }
    return response;
  },
  async fetchProductList({ dispatch, commit }, { payload, append = false }) {
    let response: PublicListCustomerProductResponse = {};
    commit('setLoadingState', { name: 'productList', status: true });
    try {
      response = await VPNonTraderAPIClient.getCustomerProducts(payload);
      commit('setProductList', { productList: response?.customer_products, append });
      commit('setProductListMeta', response?.meta?.offset_pagination);
    } catch (error) {
      commit('setProductList', []);
      commit('setProductListMeta', {});
      dispatch('catchError', { error });
    } finally {
      commit('setLoadingState', { name: 'productList', status: false });
    }
    return response;
  },
  async fetchAllProductList({ dispatch, getters }, { payload }) {
    let offset = 0;
    while (true) {
      await dispatch('fetchProductList', {
        payload: {
          ...payload,
          offset,
        },
        append: true,
      });
      if (getters.getProductList.length === 0 || getters.getProductList.length >= getters.getProductListMeta?.total) {
        break;
      }
      offset += 100;
    }
  },
  async fetchVoucherList(
    { dispatch, commit },
    { productType, productCode, requestAmount, checkValidity = true, limit = 100, offset = 0, isAllVouchers = false, paymentMethodType }
  ) {
    let response: any = [];
    commit('setLoadingState', { name: 'voucherList', status: true });
    try {
      let voucherList: any = [];
      if (isAllVouchers) {
        const { vouchers } = await VPNonTraderAPIClient.getAllVouchers(productType, limit, offset);
        voucherList = vouchers;
      } else {
        const { voucherkus } = await VPNonTraderAPIClient.getVoucherku(productType, limit, offset);
        voucherList = voucherkus;
      }

      const counts = countBy(voucherList, v => v.voucher_code);
      voucherList = uniqBy(voucherList as IVoucher[], v => v.voucher_code);
      if (voucherList.length > 0) {
        let validatedVouchers: SharedVoucher[];

        if (checkValidity) {
          const { vouchers } = await VPNonTraderAPIClient.bulkValidateVoucher(
            voucherList?.map(voucher => voucher.voucher_code || '')?.filter(code => code),
            productCode,
            'PAYMENT_METHOD_TYPE_WALLET',
            requestAmount,
            paymentMethodType
          );
          validatedVouchers = vouchers as SharedVoucher[];
        }

        response = voucherList.map(voucher => {
          return {
            ...voucher,
            validity: validatedVouchers?.find(
              validatedVoucher => validatedVoucher.voucher_code === voucher.voucher_code
            ),
            quantity: counts[voucher.voucher_code as string] || 0,
          };
        });

        commit('setVoucherList', response);
      } else {
        commit('setVoucherList', []);
      }
    } catch (error) {
      dispatch('catchError', { error });
    } finally {
      commit('setLoadingState', { name: 'voucherList', status: false });
    }

    return response;
  },
  async bulkValidateVoucher({ dispatch, commit }, { voucherCodes, productCode, requestAmount, paymentMethodType }) {
    let response: PublicBulkValidateVoucherResponse = {};
    commit('setLoadingState', { name: 'voucherList', status: true });
    try {
      response = await VPNonTraderAPIClient.bulkValidateVoucher(
        voucherCodes,
        productCode,
        'PAYMENT_METHOD_TYPE_WALLET',
        requestAmount,
        paymentMethodType
      );
    } catch (error) {
      dispatch('catchError', { error });
    } finally {
      commit('setLoadingState', { name: 'voucherList', status: false });
    }
    return response;
  },
  async fetchCustomerPackageCollection({ dispatch, commit }, payload) {
    let response: PublicListCustomerPackageCollectionResponse = {};
    commit('setLoadingState', { name: 'customerPackageCollections', status: true });
    try {
      response = await VPNonTraderAPIClient.getCustomerPackageCollections(payload);
      commit(
        'setCustomerPackageCollections',
        response?.customer_package_collections?.filter(
          item => item.status !== 'CUSTOMER_PACKAGE_COLLECTION_STATUS_HIDDEN'
        )
      );
    } catch (error) {
      commit('setCustomerPackageCollections', []);
      dispatch('catchError', { error });
    } finally {
      commit('setLoadingState', { name: 'customerPackageCollections', status: false });
    }
    return response;
  },

  async fetchCustomerPackageCollectionWithProduct({ dispatch, commit }, payload) {
    let response: PublicListCustomerPackageCollectionResponse = {};
    commit('setLoadingState', { name: 'customerPackageCollections', status: true });
    try {
      response = await VPNonTraderAPIClient.getCustomerPackageCollections({
        vendor_id: payload.vendor_id,
      });
      const collections =
        response?.customer_package_collections?.filter(
          item => item.status !== 'CUSTOMER_PACKAGE_COLLECTION_STATUS_HIDDEN'
        ) || [];
      const lowestPriceByNominal = await getLowestPrices(collections, payload.phone_number, payload.vendor_id);
      const mappedCollections = collections.map(collection => ({
        ...collection,
        lowestPrice: lowestPriceByNominal[collection.nominal!]?.price || null,
      }));

      commit('setCustomerPackageCollections', mappedCollections);
    } catch (error) {
      commit('setCustomerPackageCollections', []);
      dispatch('catchError', { error });
    } finally {
      commit('setLoadingState', { name: 'customerPackageCollections', status: false });
    }
    return response;
  },
};

async function getLowestPrices(collections, phoneNumber, vendorId) {
  const MINUTES = 60 * 1000; // 1 minute in milliseconds
  const CACHE_DURATION = 5 * MINUTES; // 5 minutes
  const cacheKey = `product-list-cache-${phoneNumber}-${vendorId}`;
  const cacheTimestampKey = `${cacheKey}-timestamp`;

  const cachedData = localStorage.getItem(cacheKey);
  const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
  const isValidCache = cachedData && cacheTimestamp && Date.now() - parseInt(cacheTimestamp) < CACHE_DURATION;

  if (isValidCache) {
    return JSON.parse(cachedData!);
  }

  const batches = transformArrayMixin.methods.splitArraytoChunk(collections, 5);
  const productResponses = await Promise.all(
    batches.flatMap(batch =>
      batch.map(async collection => {
        return await VPNonTraderAPIClient.getCustomerProducts({
          phone_number: phoneNumber,
          product_type: 'prepaid-phone-topup',
          vendor_id: collection.vendor_id,
          customer_package_collection_id: collection.id,
        });
      })
    )
  );

  const lowestPrices = productResponses
    .map(response => {
      const products = response.customer_products?.filter(p => p.status === 'available') || [];
      return products.length
        ? products.reduce((min, p) => (parseFloat(p.price) < parseFloat(min.price) ? p : min))
        : null;
    })
    .filter(Boolean)
    .reduce((acc, product) => {
      acc[product.nominal] = { price: product.price, code: product.code };
      return acc;
    }, {});

  localStorage.setItem(cacheKey, JSON.stringify(lowestPrices));
  localStorage.setItem(cacheTimestampKey, Date.now().toString());
  setTimeout(() => {
    localStorage.removeItem(cacheKey);
    localStorage.removeItem(cacheTimestampKey);
  }, CACHE_DURATION);

  return lowestPrices;
}

const mutations = {
  setCustomerList(state, customerList) {
    state.customerList = customerList;
  },
  setVendorList(state, vendorList) {
    state.vendorList = vendorList;
  },
  setChosenProduct(state, chosenProduct) {
    state.chosenProduct = chosenProduct;
  },
  setProductList(state, { productList = [], append = false }) {
    state.productList = append ? [...state.productList, ...productList] : productList;
  },
  setProductListMeta(state, metadata) {
    state.productListMeta = metadata;
  },
  setVoucherList(state, voucherList) {
    state.voucherList = voucherList;
  },
  setLoadingState(state, loading) {
    state.loading[loading.name] = loading.status;
  },
  resetState(state) {
    state.customerList = JSON.parse(JSON.stringify(initialState.customerList));
    state.vendorList = JSON.parse(JSON.stringify(initialState.vendorList));
    state.chosenProduct = JSON.parse(JSON.stringify(initialState.chosenProduct));
    state.productList = JSON.parse(JSON.stringify(initialState.productList));
    state.productListMeta = JSON.parse(JSON.stringify(initialState.productListMeta));
    state.customerPackageCollections = JSON.parse(JSON.stringify(initialState.customerPackageCollections));
    state.voucherList = JSON.parse(JSON.stringify(initialState.voucherList));
  },
  setCustomerPackageCollections(state, customerPackageCollections) {
    state.customerPackageCollections = customerPackageCollections;
  },
  updateProductList(state, { productId, updates }) {
    const index = state.productList.findIndex(product => product.id === productId);
    if (index >= 0) {
      state.productList = [
        ...state.productList.slice(0, index),
        { ...state.productList[index], ...updates },
        ...state.productList.slice(index + 1),
      ];
    }
  },
};

export default {
  state: () => ({ ...initialState }),
  getters,
  actions,
  mutations,
};
